import React, { useEffect, useState, useRef } from 'react';
import { getShops, createShop, updateShop, deleteShop, reorderShops } from '../../services/api';
import toast from 'react-hot-toast';

const dragHandleStyle = {
  cursor: 'grab',
  fontSize: 16,
  userSelect: 'none',
};

export default function Shops() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });
  const [saving, setSaving] = useState(false);

  // Drag-reorder refs
  const [draggingId, setDraggingId] = useState(null);
  const dragItem = useRef(null);
  const dragBackup = useRef(null);
  const lastTarget = useRef(null);
  const dropDone = useRef(false);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    getShops(showInactive)
      .then((res) => setShops(res.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Σφάλμα'))
      .finally(() => { if (!silent) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '', phone: '' });
    setShowModal(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, address: s.address || '', phone: s.phone || '' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Συμπληρώστε όνομα');
    setSaving(true);
    try {
      if (editing) {
        await updateShop(editing.id, form);
        toast.success('Ενημερώθηκε');
      } else {
        await createShop(form);
        toast.success('Δημιουργήθηκε');
      }
      setShowModal(false);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (s) => {
    if (!window.confirm(`Απενεργοποίηση: ${s.name};`)) return;
    try {
      await deleteShop(s.id, false);
      toast.success('Απενεργοποιήθηκε');
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const reactivate = async (s) => {
    try {
      await updateShop(s.id, { is_active: true });
      toast.success('Ενεργοποιήθηκε');
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  const hardDelete = async (s) => {
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ: ${s.name};\nΑυτή η ενέργεια δεν αναιρείται.`)) return;
    try {
      await deleteShop(s.id, true);
      toast.success('Διαγράφηκε οριστικά');
      load(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Σφάλμα'); }
  };

  // Drag-reorder handlers
  const onDragStart = (e, s) => {
    dragItem.current = s; dragBackup.current = null;
    lastTarget.current = null; dropDone.current = false;
    setDraggingId(s.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onDragOver = (e, target) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragItem.current || dragItem.current.id === target.id) return;
    if (lastTarget.current === target.id) return;
    setShops((prev) => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const fi = prev.findIndex((x) => x.id === dragItem.current.id);
      const ti = prev.findIndex((x) => x.id === target.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...prev]; n.splice(fi, 1); n.splice(ti, 0, dragItem.current);
      lastTarget.current = target.id;
      return n;
    });
  };
  const onDrop = (e) => { e.preventDefault(); dropDone.current = true; };
  const onDragEnd = async () => {
    const wasDrop = dropDone.current; const backup = dragBackup.current;
    setDraggingId(null); dragItem.current = null;
    lastTarget.current = null; dropDone.current = false;
    if (!wasDrop) { if (backup) setShops(backup); dragBackup.current = null; return; }
    dragBackup.current = null;
    const ids = shops.map((s) => s.id);
    try {
      await reorderShops(ids);
    } catch {
      toast.error('Σφάλμα σειράς');
      if (backup) setShops(backup);
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div><h2>🏪 Καταστήματα</h2><p>Διαχείριση καταστημάτων</p></div>
          <div className="flex-row">
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέο Κατάστημα</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="page-loading"><span className="loading-spinner dark" /></div>
            ) : shops.length === 0 ? (
              <div className="empty-state"><div>🏪</div><p>Δεν υπάρχουν καταστήματα</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shops.map((s) => {
                  const isDragging = draggingId === s.id;
                  return (
                    <div
                      key={s.id}
                      onDragOver={(e) => onDragOver(e, s)}
                      onDrop={onDrop}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px',
                        border: '1px solid var(--gray-100)',
                        borderRadius: 8,
                        background: isDragging ? '#dcfce7' : (s.is_active ? 'white' : 'var(--gray-50)'),
                        opacity: isDragging ? 0.5 : (s.is_active ? 1 : 0.6),
                      }}
                    >
                      <span draggable onDragStart={(e) => onDragStart(e, s)} onDragEnd={onDragEnd}
                        style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}>⠿</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, color: 'var(--gray-800)' }}>
                          {s.name}
                          {!s.is_active && <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 6 }}>(ανενεργό)</span>}
                        </div>
                        {(s.address || s.phone) && (
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {s.address && <span>{s.address}</span>}
                            {s.address && s.phone && <span> · </span>}
                            {s.phone && <span>📞 {s.phone}</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex-row">
                        {s.is_active ? (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => softDelete(s)}>🗑</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => reactivate(s)} title="Επαναφορά">↻</button>
                            <button className="btn btn-danger btn-sm" onClick={() => hardDelete(s)} title="Οριστική Διαγραφή">❌</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{editing ? 'Επεξεργασία Καταστήματος' : 'Νέο Κατάστημα'}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <div className="form-group">
                <label className="form-label">Όνομα *</label>
                <input className="form-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Διεύθυνση</label>
                <input className="form-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Τηλέφωνο</label>
                <input className="form-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Άκυρο</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Αποθήκευση'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
